import { useCallback } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AddToWaitlistRequest,
  AddToWaitlistResponse,
  ListWaitlistQuery,
  ListWaitlistResponse,
  RemoveFromWaitlistRequest,
  UpdateWaitlistRequest,
  WaitlistEntry,
  WaitlistEntryRefreshResponse,
  WaitlistRefreshResponse,
} from '@vagusx/clients';
import { getWaitlistClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { waitlistSyncingRefetchInterval } from '@/lib/ehr-sync-state';

/** A page of waitlist entries (ADR-271). */
export function useWaitlist(query: ListWaitlistQuery): UseQueryResult<ListWaitlistResponse> {
  return useQuery({
    queryKey: queryKeys.waitlist.list(query as Record<string, unknown>),
    queryFn: () => getWaitlistClient().list(query),
    placeholderData: keepPreviousData,
    // Watch a new entry or a re-sync reach the EHR.
    refetchInterval: (q) => waitlistSyncingRefetchInterval(q.state.data?.items ?? []),
  });
}

/** How often an entry is re-read while a push someone asked for is under way. */
const PUSH_POLL_MS = 2000;

/**
 * One entry. `pollWhileQueued` re-reads it every couple of seconds while its
 * change is still queued for the EHR: set while a "Send to EHR now" is under
 * way, since the push itself runs in the background.
 */
export function useWaitlistEntry(
  id: string | undefined,
  options: { pollWhileQueued?: boolean } = {}
): UseQueryResult<WaitlistEntry> {
  return useQuery({
    queryKey: queryKeys.waitlist.detail(id ?? ''),
    queryFn: () => getWaitlistClient().getById(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      options.pollWhileQueued && query.state.data?.ehr.syncStatus === 'pending'
        ? PUSH_POLL_MS
        : waitlistSyncingRefetchInterval(query.state.data ? [query.state.data] : []),
  });
}

function useInvalidateWaitlist(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.waitlist.all });
}

export function useAddToWaitlist(): UseMutationResult<
  AddToWaitlistResponse,
  Error,
  AddToWaitlistRequest
> {
  const invalidate = useInvalidateWaitlist();
  return useMutation({
    mutationFn: (data: AddToWaitlistRequest) => getWaitlistClient().add(data),
    onSuccess: invalidate,
    // The form shows a refusal under its field, or toasts it (WaitlistEntryForm).
    meta: { suppressGlobalError: true },
  });
}

export function useUpdateWaitlistEntry(): UseMutationResult<
  WaitlistEntry,
  Error,
  { id: string; data: UpdateWaitlistRequest }
> {
  const invalidate = useInvalidateWaitlist();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWaitlistRequest }) =>
      getWaitlistClient().update(id, data),
    onSuccess: invalidate,
    // The form shows a refusal under its field, or toasts it (WaitlistEntryForm).
    meta: { suppressGlobalError: true },
  });
}

export function useRemoveFromWaitlist(): UseMutationResult<
  WaitlistEntry,
  Error,
  { id: string; data: RemoveFromWaitlistRequest }
> {
  const invalidate = useInvalidateWaitlist();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RemoveFromWaitlistRequest }) =>
      getWaitlistClient().remove(id, data),
    onSuccess: invalidate,
  });
}

/** Re-read the lists (e.g. once a push has settled, so the row's sync icon follows). */
export function useInvalidateWaitlistLists(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.waitlist.lists() }),
    [queryClient]
  );
}

/** Put the entry an EHR action answered with in the cache, and re-read the lists. */
function useApplyEntry(): (entry: WaitlistEntry) => Promise<void> {
  const queryClient = useQueryClient();
  const invalidateLists = useInvalidateWaitlistLists();
  return (entry) => {
    queryClient.setQueryData(queryKeys.waitlist.detail(entry.id), entry);
    return invalidateLists();
  };
}

/**
 * Send one entry's queued change to the EHR now (ADR-277). Resolves with the
 * entry as queued, which replaces the cached copy so a poll starts from it.
 */
export function usePushWaitlistEntry(): UseMutationResult<WaitlistEntry, Error, string> {
  const applyEntry = useApplyEntry();
  return useMutation({
    mutationFn: (id: string) => getWaitlistClient().pushToEhr(id),
    onSuccess: applyEntry,
  });
}

/** Re-read one entry from the EHR now ("Check in EHR"). */
export function useRefreshWaitlistEntry(): UseMutationResult<
  WaitlistEntryRefreshResponse,
  Error,
  string
> {
  const applyEntry = useApplyEntry();
  return useMutation({
    mutationFn: (id: string) => getWaitlistClient().refreshEntry(id),
    onSuccess: ({ entry }) => applyEntry(entry),
  });
}

/** Read the EHR's waitlist now instead of waiting for the scheduled read. */
export function useRefreshWaitlist(): UseMutationResult<WaitlistRefreshResponse, Error, void> {
  const invalidate = useInvalidateWaitlist();
  return useMutation({
    mutationFn: () => getWaitlistClient().refresh(),
    onSuccess: invalidate,
  });
}
